import { useState } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { logout, selectCurrentUser } from '../../slices/authSlice';
import logo from '../../assets/logo.svg';
import styles from './StaffLayout.module.css';

const NAV_BY_ROLE = {
  admin: [
    { to: '/admin/organizations', label: 'Organizations', icon: 'pi-building' },
    { to: '/admin/email-domains', label: 'Email domains', icon: 'pi-at' },
    { to: '/admin/requests', label: 'Requests', icon: 'pi-inbox' },
    { to: '/admin/groups', label: 'Groups', icon: 'pi-bell' },
    { to: '/admin/system-notifications', label: 'System Notifications', icon: 'pi-shield' },
    { to: '/admin/users', label: 'Users', icon: 'pi-users' },
    { to: '/admin/metrics', label: 'Metrics', icon: 'pi-chart-bar' },
  ],
  manager: [
    { to: '/manager/groups', label: 'Groups', icon: 'pi-bell' },
    { to: '/manager/subscribers', label: 'Subscribers', icon: 'pi-users' },
    { to: '/manager/hosts', label: 'Hosts', icon: 'pi-user' },
    { to: '/manager/metrics', label: 'Metrics', icon: 'pi-chart-bar' },
  ],
  host: [
    { to: '/host/giveaways', label: 'Giveaways', icon: 'pi-gift' },
    { to: '/host/schedule', label: 'Schedule', icon: 'pi-calendar' },
  ],
};

const SIDEBAR_TITLE_BY_ROLE = {
  admin: 'Admin Portal',
  manager: 'Manager Portal',
  host: 'Host Portal',
};

function NavLinks({ links, onNavigate }) {
  return (
    <nav className={styles.nav}>
      {links.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          onClick={onNavigate}
          className={({ isActive }) => clsx(styles.navLink, isActive && styles.navLinkActive)}
        >
          <i className={`pi ${link.icon}`} />
          {link.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function StaffLayout() {
  const user = useSelector(selectCurrentUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const links = NAV_BY_ROLE[user?.role] ?? [];
  const title = SIDEBAR_TITLE_BY_ROLE[user?.role];

  function handleLogout() {
    dispatch(logout());
    navigate('/login', { replace: true });
  }

  return (
    <div className={styles.layout}>
      <header className={styles.header}>
        <Button
          icon="pi pi-bars"
          text
          className={styles.menuBtn}
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open menu"
        />
        <Link to="/" className={styles.logo}>
          <img src={logo} alt="PulseLoop" className={styles.logoImg} />
        </Link>
        <div className={styles.headerRight}>
          <span className={styles.userInfo}>
            {user?.email} <span className={styles.userRole}>({user?.role})</span>
          </span>
          <Button
            label="Log out"
            text
            className={styles.logoutBtn}
            onClick={handleLogout}
          />
        </div>
      </header>

      <aside className={styles.sidebar}>
        <h2 className={styles.sidebarTitle}>{title}</h2>
        <NavLinks links={links} />
      </aside>

      <Sidebar
        visible={mobileNavOpen}
        onHide={() => setMobileNavOpen(false)}
        className={styles.mobileSidebar}
      >
        <h2 className={styles.sidebarTitle}>{title}</h2>
        <NavLinks links={links} onNavigate={() => setMobileNavOpen(false)} />
      </Sidebar>

      <div className={styles.content}>
        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
